import React, { useContext, useEffect, useState } from "react";
import { DataSourceBrowserModal } from "@components/DataSourceBrowser/DataSourceBrowserModal";
import { askQuestion, getSampleQuestions } from "@query-engine-service";
import { FilteredOrigins, IDataSource, IQuery } from "@types";
import { generateInitialQuery, getSchemaForSource, prepareQuery } from "@utils";
import { Button, TextArea } from "uxp/components";
import { QueryBuilderContext } from "@query-builder/QueryBuilderContext";
import { Loading, LoadingOverlay } from "@components/Loading/Loading";
import { trackAnalytics } from "../../analytics";
import './InitialScreen.scss';

interface IInitialScreenProps {
    query: IQuery,
    onSetQuery: (query: IQuery) => void,
}

export const InitialScreen: React.FC<IInitialScreenProps> = (props) => {
    const context = useContext(QueryBuilderContext);

    const [view, setView] = useState<'init' | 'source'>('init');
    const [origins, setOrigins] = useState<FilteredOrigins>([]);
    const [question, setQuestion] = useState('');
    const [processingQuestion, setProcessingQuestion] = useState(false);
    const [loadingSamples, setLoadingSamples] = useState(false);
    const [samples, setSamples] = useState<string[]>([]);

    useEffect(() => {
        if (props?.query?.sourceOnly) {
            setView('source');
            if (props?.query?.sources?.[0]?.id?.startsWith('query')) setOrigins(['query']);
            else setOrigins([]);
        }
    }, [props?.query]);

    useEffect(() => {
        if (view === 'init') {
            setLoadingSamples(true);
            getSampleQuestions(context)
                .then(x => setSamples(x))
                .catch(console.error)
                .finally(() => { setLoadingSamples(false) });
        }
    }, [view, context]);

    function getSource(): IDataSource {
        if (props?.query?.sourceOnly) {
            return props?.query?.sources?.[0] || null;
        }
        return null;
    }

    async function submitQuestion(q: string) {
        if (!q.trim()) return;

        try {
            setProcessingQuestion(true);
            let p = await askQuestion(context, q);
            if (!p) {
                setProcessingQuestion(false);
                throw 'Um. Something went wrong. Can we try rephrasing that question?';
            }
            if (p.question) {
                alert(p.question);
                setProcessingQuestion(false);
                return;
            }

            if (p.steps && p.sources) {
                props.onSetQuery(await prepareQuery(context, p, origins));
            }
        } catch (e) {
            setProcessingQuestion(false);
            alert(e);
        }
    }

    async function addInitialSource(source: IDataSource) {
        // get source schema
        const schema = await getSchemaForSource(context, { sources: [source], steps: [] }, source.id);
        source.schema = schema;
        const sourceOnly = origins?.length > 0;
        props.onSetQuery({
            sources: [source],
            steps: [],
            sourceOnly: sourceOnly
        });

        setView('init');
        setOrigins([]);
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (question.trim()) {
                submitQuestion(question);
                trackAnalytics(context.uxpContext, 'ask-question', { message: question });
            }
        }
    };

    const handleSampleClick = (sampleQuestion: string) => {
        setQuestion(sampleQuestion);
        submitQuestion(sampleQuestion);
        trackAnalytics(context.uxpContext, 'pre-loaded-question', { message: sampleQuestion });
    };

    if (view === 'source') {
        return <DataSourceBrowserModal
            show={true}
            onClose={() => { setView('init'); setOrigins([]) }}
            source={getSource()}
            onSelect={addInitialSource}
            showCloseButton={true}
            origins={origins}
            query={generateInitialQuery()}
        />;
    }

    return (
        <div className="query-editor-initial-screen">
            {processingQuestion && <LoadingOverlay message='Processing question and generating the steps...' />}

            <div className="top-section">
                <h1>Data Explorer</h1>
                <p>Lucy's data explorer is a tool for data analysis.</p>
                <p>Choose your data source. Filter, aggregate, enrich, transform it.</p>
            </div>

            <div className="middle-section">
                <h2>Ask a question to get started</h2>
                <div className="input-container">
                    <TextArea
                        readOnly={processingQuestion}
                        value={question}
                        onChange={setQuestion}
                        onKeyDown={handleKeyDown}
                        rows={3}
                    />
                </div>

                <div className="sample-questions">
                    {loadingSamples ? (
                        <div className="loading-container">
                            <Loading message='Loading sample questions...' />
                        </div>
                    ) : (
                        samples.map((sampleQuestion, i) => (
                            <div
                                onClick={() => handleSampleClick(sampleQuestion)}
                                className="sample-question"
                                key={i}
                            >
                                {sampleQuestion}
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="bottom-section">
                <div className="buttons-container">
                    <h2>or select a data source to build a new query</h2>
                    <Button
                        title="Select a data source"
                        className="data-source-button"
                        onClick={() => setView('source')}
                        icon="fas plus"
                    />
                </div>
                {context?.canSelectSavedQuerries ?
                    <div className="buttons-container">
                        <h2>or choose a saved query</h2>
                        <Button
                            title="Select a saved query"
                            className="saved-query-button"
                            onClick={() => { setView('source'); setOrigins(['query']) }}
                            icon="fas bookmark"
                        />
                    </div>
                    : null}
            </div>
        </div>
    );
};